import { ActionButtons, Button, Icon, PlayButton, Text } from '@southfields-digital/mxmz-react-ui';
import classnames from 'classnames';

import CanvasOutputPicker from 'src/components/CanvasOutputPicker';
import { useAutoUpdateSnapshot } from 'src/features/dataConnector/application/autoUpdateManager';

import styles from './Graphic.module.scss';
import { IGraphicProps } from './types';

function Graphic(props: IGraphicProps) {
  const {
    variant,
    id,
    text,
    subText,
    live,
    preview,
    onLive,
    graphic,
    canConnect = true,
    editable = true,
    icon = 'sort',
    isConcept = false,
    showIsConcept = false,
    onCanvasOverride,
    playlistGraphicCanvasOverride,
  } = props;

  const active = variant === 'static' ? false : props.active;
  const isQuickActive = variant === 'static' ? false : props.isQuick;
  const onClick = variant === 'static' ? () => {} : props.onClick;
  const onDuplicate = variant === 'static' ? () => {} : props.onDuplicate;
  const onQuickify = variant === 'static' ? () => {} : props.onQuickify;
  const onDelete = variant === 'static' ? () => {} : props.onDelete;
  const onConnect = variant === 'static' ? () => {} : props.onConnect;
  const onConcept = variant === 'static' ? () => {} : props.onConcept;
  const parent = variant === 'static' ? false : props.parent;

  const showActionButtons = variant !== 'static' && editable !== false;

  const canConnectToParent = variant === 'static' ? false : canConnect;
  const autoUpdateSnapshot = useAutoUpdateSnapshot(id);
  const isAutoUpdating = autoUpdateSnapshot.isEnabled;

  const handleToggleLive = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onLive(e);
  };

  return (
    <div
      className={classnames(
        styles.Graphic,
        variant === 'default' && {
          [styles.Active]: active && !preview && !live,
          [styles.PreviewAndLive]: preview && live,
          [styles.Preview]: preview && !live,
          [styles.Live]: live && !preview,
        }
      )}
      key={id}
    >
      {variant === 'default' && icon && editable ? (
        <div className="pl-3 pr-2 text-gray-500">
          <Icon icon={icon} weight="bold" size={20} />
        </div>
      ) : null}
      <div
        className={classnames(styles.Content, {
          'pl-3': variant === 'default' && (!icon || !editable),
        })}
        onClick={onClick}
      >
        <PlayButton
          variant="live"
          className={styles.PlayButton}
          onClick={handleToggleLive}
          isRunning={live}
          hasIcon
        />

        <div className={styles.Label}>
          <div className={styles.GraphicName}>
            <div className={styles.GraphicNameContent}>
              <Text as="p" lineClamp="clamp-1">
                {text}
              </Text>
              {graphic?.layer?.name && (
                <Text as="span" className={styles.GraphicNameLabel}>
                  {graphic.layer.name}
                </Text>
              )}
              {graphic?.name && (
                <Text as="span" className={styles.GraphicNameLabel} title={graphic.name}>
                  {graphic.name}
                </Text>
              )}
            </div>
            {isAutoUpdating && (
              <div>
                <Icon icon="broadcast" size={18} color="#5B5D70" />
              </div>
            )}
            {isConcept && showIsConcept ? (
              <span className="relative">
                <Icon icon="arrow-clockwise" size={24} className="text-gray-500" />
                <Icon
                  icon="x"
                  weight="bold"
                  size={10}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-500"
                />
              </span>
            ) : null}
          </div>
        </div>

        {graphic && (
          <div className="flex-1 justify-items-end pr-12">
            <CanvasOutputPicker
              graphic={graphic}
              playlistGraphicId={id}
              canvasOverride={playlistGraphicCanvasOverride}
              onUpdate={onCanvasOverride}
              isLive={live}
            />
          </div>
        )}

        {showActionButtons && (
          <div className={styles.Options}>
            <ActionButtons isActive={active} text={subText}>
              {showIsConcept && (
                <Button
                  iconProps={{ icon: 'arrow-clockwise', size: 24 }}
                  color={isConcept ? 'primaryLight' : 'darkGray'}
                  onClick={onConcept}
                  className="relative"
                >
                  <Icon icon="x" weight="bold" size={10} className="absolute" />
                </Button>
              )}
              {canConnectToParent && (
                <Button
                  iconProps={{ icon: 'arrow-elbow-left-up', weight: 'bold' }}
                  color={parent ? 'primaryLight' : 'darkGray'}
                  onClick={onConnect}
                />
              )}
              <Button
                iconProps={{ icon: 'quickbutton', weight: 'bold' }}
                color={isQuickActive ? 'primaryLight' : 'darkGray'}
                onClick={onQuickify}
              />
              <Button
                iconProps={{ icon: 'copy', weight: 'bold' }}
                color="darkGray"
                onClick={onDuplicate}
              />
              <Button
                iconProps={{ icon: 'trash', weight: 'bold' }}
                color="danger"
                onClick={onDelete}
              />
            </ActionButtons>
          </div>
        )}
      </div>
    </div>
  );
}

export default Graphic;
